// トップの立ち絵を Live2D 風に動かす（honi-chan の honi-rig.js を1枚絵向けに移植）。
//
// 1枚の立ち絵を格子メッシュに貼り、頂点シェーダで変形する。重み画像（tools/art/live-rig.py で作る）：
//   R＝頭に付いて動く量（首の支点まわりに傾く）
//   G＝揺れる量（付け根 0 → 先 1）、B＝揺れのグループ（0 髪・房飾り／0.5 布／1 持ち物）
// 呼吸・体のゆらぎは全体、揺れはグループごとのばね（頭・体の動きに遅れる）＋常時のそよぎ。
// まばたきは目のパッチを2Dキャンバスで重ねてテクスチャを作り直す（目の状態が変わった時だけ）。
// タップで笑って弾む（rig.json に別ポーズがあれば、ポンと切り替えて少しして戻る。今は元のポーズだけ）。
//
// 茶屋のたまき（okami）は胸から上の表情4つ：setPose で表情を替える（ふわっと入れ替える。光らせない）。
// 押すとにっこり（rig.json の tap）して、少しして元の表情に戻る。動きは rig.json の motion の割合で小さめ。
// rig.json の bust（楕円の範囲）があれば胸を揺らす：呼吸で少し持ち上がり、体の動き・表情の切り替えに少し遅れて弾む（頭に付く所は動かさない）。
//
// 戻すとき：LIVE_PORTRAITS を false にすると、これまでどおりの静止画になる。
import type { CharId } from '@hyakki/core';

export const LIVE_PORTRAITS = true;
/** 動かせる絵：味方4人と、お問い合わせの女将 */
export type LiveId = CharId | 'okami';
/** 素材がそろったキャラ（public/art/live/<id>/rig.json） */
const LIVE_CHARS: readonly LiveId[] = ['akari', 'kazamaru', 'suzune', 'yuzuha', 'okami'];

const VS = `#version 300 es
in vec2 a_pos;
uniform vec2 u_size;
uniform sampler2D u_w;
uniform vec2 u_pivot;
uniform float u_headRot;
uniform vec2 u_headOff;
uniform float u_time;
uniform vec2 u_sw0, u_sw1, u_sw2;
uniform vec3 u_id0, u_id1, u_id2;
uniform float u_wind;
uniform float u_breath;
uniform vec2 u_hip;
uniform float u_bodyRot;
uniform vec2 u_bodyOff;
uniform float u_squash;
uniform vec2 u_bustC0;
uniform vec2 u_bustC1;
uniform vec2 u_bustR;
uniform vec2 u_bust;
uniform mat3 u_view;
out vec2 v_uv;
vec2 rot(vec2 p, vec2 c, float a) {
  vec2 d = p - c; float s = sin(a), k = cos(a);
  return c + vec2(k * d.x - s * d.y, s * d.x + k * d.y);
}
void main() {
  vec2 uv = a_pos / u_size;
  vec4 w = texture(u_w, uv);
  vec2 p = a_pos;
  p = mix(p, rot(p, u_pivot, u_headRot) + u_headOff, w.r);
  float g = w.b;
  float k0 = 1.0 - smoothstep(0.2, 0.45, g);
  float k2 = smoothstep(0.65, 0.9, g);
  float k1 = 1.0 - k0 - k2;
  vec2 sw = k0 * u_sw0 + k1 * u_sw1 + k2 * u_sw2;
  float tw = 6.2832 * u_time;
  float i0 = u_id0.x * sin(tw / u_id0.y + u_id0.z + a_pos.x * 0.016 + a_pos.y * 0.007 - w.g * 1.2);
  float i1 = u_id1.x * sin(tw / u_id1.y + u_id1.z + a_pos.x * 0.004 - w.g * 1.6);
  float i2 = u_id2.x * sin(tw / u_id2.y + u_id2.z - w.g * 1.1);
  float idle = k0 * i0 + k1 * i1 + k2 * i2;
  float ph = u_time * 2.4 + a_pos.y * 0.015 + a_pos.x * 0.03 + g * 2.3;
  vec2 wind = vec2(sin(ph), 0.3 * sin(ph * 1.3 + 1.0)) * u_wind;
  p += w.g * (sw + vec2(idle, -abs(idle) * 0.15) + wind);
  p.y -= u_breath * smoothstep(u_size.y * 0.98, u_size.y * 0.4, a_pos.y);
  // 胸：左右の楕円で同じだけ動かす（中ほどがいちばん動き、縁へなめらかに 0）。頭に付いて動く垂れ髪の所は除く
  // （頭の重みは周りへぼかしてあるので、強い所だけを除いて左右の差が出ないように）
  if (u_bustR.x > 0.0) {
    float e = min(length((a_pos - u_bustC0) / u_bustR), length((a_pos - u_bustC1) / u_bustR));
    p += u_bust * (1.0 - smoothstep(0.25, 1.0, e)) * (1.0 - smoothstep(0.55, 0.9, w.r));
  }
  // ポーズを切り替えた瞬間の弾み：足元を支点に縦につぶして横に広げる
  float foot = u_size.y * 0.97;
  p.y = foot + (p.y - foot) * (1.0 - u_squash);
  p.x = u_hip.x + (p.x - u_hip.x) * (1.0 + u_squash * 0.6);
  p = rot(p, u_hip, u_bodyRot) + u_bodyOff;
  vec3 c = u_view * vec3(p, 1.0);
  gl_Position = vec4(c.xy, 0.0, 1.0);
  v_uv = uv;
}`;

const FS = `#version 300 es
precision mediump float;
in vec2 v_uv;
uniform sampler2D u_tex;
uniform float u_alpha;
uniform float u_flash;
out vec4 o;
void main() { vec4 c = texture(u_tex, v_uv); c.rgb += u_flash * c.a; o = c * u_alpha; }`;

type Patch = { file: string; x: number; y: number };
type PoseDef = { image: string; weights: string; neck?: [number, number]; hip?: [number, number]; eyes?: Partial<Record<'half' | 'closed' | 'smile', Patch>>; react?: 'smile' | 'wink' };
/** sway：揺れのグループごとの強さ（重い持ち物は小さく）。省略は 1 */
/** motion：動き（揺れ・呼吸・首の動き）の大きさの割合（胸から上の絵は小さめ）。tap：押したときに替える表情（押すと戻る） */
/** bust：胸の揺れの範囲（左右の楕円の中心 c・共通の半径 r、絵の px）。省略なら揺らさない */
type RigDef = { size: [number, number]; neck: [number, number]; hip: [number, number]; sway?: Partial<Record<'hair' | 'cloth' | 'prop', number>>; motion?: number; tap?: string; bust?: { c: [[number, number], [number, number]]; r: [number, number] }; poses: Record<string, PoseDef> };
type Pose = PoseDef & { name: string; img: HTMLImageElement; w: HTMLImageElement; parts: Record<string, HTMLImageElement>; tex: WebGLTexture; wTex: WebGLTexture; key: string; alpha: Uint8ClampedArray | null };

/** 揺れのグループ：k=硬さ, c=減衰, gain=動きへの反応, lim=最大振れ(px), idle=[振幅px, 周期秒, 位相], src=何に遅れるか */
const SPRINGS = {
  hair: { k: 22, c: 2.2, gain: 3.2, lim: 26, src: 'head', idle: [6, 2.3, 0] },
  cloth: { k: 9, c: 1.5, gain: 3.4, lim: 34, src: 'body', idle: [9, 3.1, 0.8] },
  prop: { k: 13, c: 1.4, gain: 2.6, lim: 22, src: 'body', idle: [5, 2.7, 1.7] },
} as const;
type SpringKey = keyof typeof SPRINGS;
const GROUPS: [SpringKey, SpringKey, SpringKey] = ['hair', 'cloth', 'prop'];
/**
 * 胸のばね（ソシャゲの立ち絵くらい弾ませる）：k=硬さ, c=減衰（小さいほど何度も弾む）, gain=体の動き・呼吸への反応, lim=最大振れ(px)、
 * lift=呼吸で持ち上がる割合、kick=表情を替えた・押したときの弾み、beat=呼吸の吸い終わりごとの小さな弾み
 */
const BUST = { y: { k: 90, c: 2.2, gain: 3.5, lim: 16 }, x: { k: 70, c: 2.6, gain: 0.8, lim: 6 }, lift: 1.0, kick: 160, beat: 45 } as const;
/** 描く範囲の余白（揺れて絵の外へはみ出す分）：左右・上は絵の大きさに対する割合、下は 0 */
export const LIVE_PAD = { x: 0.08, top: 0.06 };

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((res, rej) => {
    const im = new Image();
    im.decoding = 'async';
    im.onload = () => res(im);
    im.onerror = () => rej(new Error(`image load failed: ${src}`));
    im.src = src;
  });

function rng(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function noise1(t: number, seed: number) {
  let v = 0;
  let amp = 1;
  let f = 1;
  for (let o = 0; o < 3; o++) {
    const x = t * f + seed * 13.7 + o * 31.1;
    const i = Math.floor(x);
    const fr = x - i;
    const h = (n: number) => {
      const s = Math.sin(n * 127.1 + seed * 311.7) * 43758.5453;
      return s - Math.floor(s);
    };
    const u = fr * fr * (3 - 2 * fr);
    v += amp * ((h(i) * (1 - u) + h(i + 1) * u) * 2 - 1);
    amp *= 0.5;
    f *= 2.03;
  }
  return v / 1.75;
}
const cl01 = (x: number) => Math.max(0, Math.min(1, x));

/** タップで決めポーズに切り替えて、この秒数で元に戻る */
const POSE_HOLD = 4.2;
const SWAP = 0.07;
/** 表情の入れ替え（setPose）にかける秒数。前の表情の上に新しい表情を重ねて濃くしていく */
const FADE = 0.3;
/** 押してにっこりしたあと、元の表情に戻るまでの秒数 */
const TAP_HOLD = 2.4;

export class LivePortrait {
  readonly canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext;
  private rig: RigDef;
  private poses: Pose[] = [];
  private prog!: WebGLProgram;
  private vao!: WebGLVertexArrayObject;
  private nIdx = 0;
  private loc: Record<string, WebGLUniformLocation | null> = {};
  private cv = document.createElement('canvas');
  private raf = 0;
  private t0 = performance.now();
  private cur = 0;
  private prev = 0;
  private swapAt = -99;
  private backAt = Infinity;
  private blinks: number[];
  private st: Record<SpringKey, { x: number; v: number; y: number; vy: number }>;
  private simT = 0;
  private prevMotion: { head: number; body: number; nod: number; bx: number; by: number } | null = null;
  /** 胸のばね（rig.json の bust があるときだけ使う） */
  private bust = { x: 0, v: 0, y: 0, vy: 0 };
  private bustBeat = -1;
  private bustOut: [number, number] = [0, 0];
  private kick = 0;
  private destroyed = false;
  private lastPick = -1;
  /** 戻り先の表情（setPose で決めたもの） */
  private home = 0;
  private fadeFrom = -1;
  private fadeAt = -99;

  static available(id: LiveId) {
    if (!LIVE_PORTRAITS || !LIVE_CHARS.includes(id)) return false;
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return true;
  }

  static async load(id: LiveId): Promise<LivePortrait | null> {
    try {
      const base = `/art/live/${id}/`;
      const rig = (await (await fetch(`${base}rig.json`)).json()) as RigDef;
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2', { premultipliedAlpha: true, alpha: true, antialias: true });
      if (!gl) return null;
      const lp = new LivePortrait(canvas, gl, rig);
      for (const [name, d] of Object.entries(rig.poses)) {
        const [img, w] = await Promise.all([loadImage(base + d.image), loadImage(base + d.weights)]);
        const parts: Record<string, HTMLImageElement> = {};
        for (const [k, p] of Object.entries(d.eyes ?? {})) if (p) parts[k] = await loadImage(base + p.file);
        lp.addPose({ ...d, name, img, w, parts });
      }
      return lp.poses.length ? lp : null;
    } catch {
      return null;
    }
  }

  private constructor(canvas: HTMLCanvasElement, gl: WebGL2RenderingContext, rig: RigDef) {
    this.canvas = canvas;
    this.gl = gl;
    this.rig = rig;
    canvas.className = 'oshi-img tall oshi-live';
    canvas.setAttribute('role', 'img');
    const [W, H] = rig.size;
    canvas.width = Math.round(W * (1 + LIVE_PAD.x * 2) * 0.5);
    canvas.height = Math.round(H * (1 + LIVE_PAD.top) * 0.5);
    canvas.style.aspectRatio = `${W * (1 + LIVE_PAD.x * 2)} / ${H * (1 + LIVE_PAD.top)}`;
    this.cv.width = W;
    this.cv.height = H;
    this.blinks = (() => {
      const r = rng(7);
      const out: number[] = [];
      let t = 1.2;
      while (t < 3600) {
        out.push(t);
        if (r() < 0.18) out.push(t + 0.32);
        t += 2.2 + r() * 3.4;
      }
      return out;
    })();
    this.st = { hair: { x: 0, v: 0, y: 0, vy: 0 }, cloth: { x: 0, v: 0, y: 0, vy: 0 }, prop: { x: 0, v: 0, y: 0, vy: 0 } };
    this.initGL();
    canvas.addEventListener('pointerup', (e) => this.onTap(e));
  }

  private initGL() {
    const gl = this.gl;
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader');
      return s;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) ?? 'link');
    this.prog = prog;
    for (const n of ['u_size', 'u_w', 'u_tex', 'u_pivot', 'u_headRot', 'u_headOff', 'u_time', 'u_sw0', 'u_sw1', 'u_sw2', 'u_id0', 'u_id1', 'u_id2', 'u_wind', 'u_breath', 'u_hip', 'u_bodyRot', 'u_bodyOff', 'u_squash', 'u_bustC0', 'u_bustC1', 'u_bustR', 'u_bust', 'u_view', 'u_alpha', 'u_flash'])
      this.loc[n] = gl.getUniformLocation(prog, n);
    const [W, H] = this.rig.size;
    const NX = 48;
    const NY = 72;
    const pos: number[] = [];
    const idx: number[] = [];
    for (let j = 0; j <= NY; j++) for (let i = 0; i <= NX; i++) pos.push((i / NX) * W, (j / NY) * H);
    for (let j = 0; j < NY; j++)
      for (let i = 0; i < NX; i++) {
        const a = j * (NX + 1) + i;
        const b = a + 1;
        const c = a + NX + 1;
        const d = c + 1;
        idx.push(a, b, c, b, d, c);
      }
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pos), gl.STATIC_DRAW);
    const al = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(al);
    gl.vertexAttribPointer(al, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
    this.nIdx = idx.length;
    gl.bindVertexArray(null);
  }

  private tex(img: TexImageSource | null, mip: boolean, premul: boolean) {
    const gl = this.gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mip ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (img) {
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, premul);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      if (mip) gl.generateMipmap(gl.TEXTURE_2D);
    }
    return t;
  }

  private addPose(p: Omit<Pose, 'tex' | 'wTex' | 'key' | 'alpha'>) {
    // 当たり判定用の不透明度（1/8 の大きさ）
    let alpha: Uint8ClampedArray | null = null;
    try {
      const c = document.createElement('canvas');
      c.width = Math.ceil(p.img.width / 8);
      c.height = Math.ceil(p.img.height / 8);
      const x = c.getContext('2d', { willReadFrequently: true })!;
      x.drawImage(p.img, 0, 0, c.width, c.height);
      alpha = x.getImageData(0, 0, c.width, c.height).data;
    } catch {
      alpha = null;
    }
    this.poses.push({ ...p, tex: this.tex(null, true, true), wTex: this.tex(p.w, false, false), key: '', alpha });
  }

  /** 枠（.oshi-figure）に入れて動かし始める。描き直しで枠が変わったら付け直すだけ（GL はそのまま） */
  attach(host: Element, label: string) {
    this.canvas.setAttribute('aria-label', label);
    if (this.canvas.parentElement !== host) host.replaceChildren(this.canvas);
    if (!this.raf && !this.destroyed) this.raf = requestAnimationFrame(this.frame);
  }

  destroy() {
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.canvas.remove();
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }

  /** 表情を替える（ふわっと入れ替える）。無い名前なら何もしない。今の表情が戻り先になる */
  setPose(name: string) {
    const i = this.poses.findIndex((p) => p.name === name);
    if (i < 0) return;
    this.home = i;
    this.backAt = Infinity;
    this.fadeTo(i, this.now());
  }

  private fadeTo(i: number, t: number) {
    if (i === this.cur) return;
    this.fadeFrom = this.cur;
    this.prev = this.cur = i;
    this.fadeAt = t;
    // 髪が少し揺れるくらいに（決めポーズの切り替えより弱く）。胸も少し弾む
    for (const k of GROUPS) this.st[k].v += (Math.random() < 0.5 ? -1 : 1) * SPRINGS[k].lim * 2.5;
    this.bust.vy -= BUST.kick * (this.rig.motion ?? 1);
  }

  /**
   * 描く大きさと位置（キャンバスの px）：余白ごと収まる大きさで、左右は真ん中・下はそろえる。
   * キャンバスの形が絵と同じなら、これまでどおり（上の余白 LIVE_PAD.top）。CSS の max-width で幅だけ詰まったときも、
   * 絵の下端がキャンバスの下端に来る（たまきの胸の切れ目を会話の窓の後ろに隠すため）
   */
  private layout(cw: number, ch: number) {
    const [W, H] = this.rig.size;
    const s = Math.min(cw / (W * (1 + LIVE_PAD.x * 2)), ch / (H * (1 + LIVE_PAD.top)));
    return { s, ox: (cw - W * s) / 2, oy: ch - H * s };
  }

  private onTap(e: PointerEvent) {
    const p = this.poses[this.cur];
    // 絵の透明なところを押したときは反応しない
    if (p.alpha) {
      const r = this.canvas.getBoundingClientRect();
      const [W, H] = this.rig.size;
      const L = this.layout(r.width, r.height);
      const ix = (e.clientX - r.left - L.ox) / L.s;
      const iy = (e.clientY - r.top - L.oy) / L.s;
      const ax = Math.floor(ix / 8);
      const ay = Math.floor(iy / 8);
      const aw = Math.ceil(W / 8);
      if (ix < 0 || iy < 0 || ix >= W || iy >= H || (p.alpha[(ay * aw + ax) * 4 + 3] ?? 0) < 40) return;
    }
    const t = this.now();
    // 表情を替えるキャラ：押すと tap の表情にして、少しして戻る
    if (this.rig.tap) {
      const tap = this.poses.findIndex((x) => x.name === this.rig.tap);
      if (tap < 0 || t - this.fadeAt < 0.5) return;
      if (this.cur === tap && tap !== this.home) {
        this.backAt = Infinity;
        this.fadeTo(this.home, t);
      } else {
        this.fadeTo(tap, t);
        this.swapAt = t;
        if (tap !== this.home) this.backAt = t + TAP_HOLD;
      }
      return;
    }
    if (t - this.swapAt < 0.5) return;
    const others = this.poses.map((_, i) => i).filter((i) => i !== 0 && i !== this.lastPick);
    const next = this.cur !== 0 || this.poses.length < 2 ? 0 : (others[Math.floor(Math.random() * others.length)] ?? 1);
    this.swapTo(next, t);
  }

  private swapTo(i: number, t: number) {
    if (i === this.cur) {
      this.kick = 1;
      this.swapAt = t;
      return;
    }
    this.prev = this.cur;
    this.cur = i;
    if (i) this.lastPick = i;
    this.swapAt = t;
    this.kick = 1;
    this.backAt = i === 0 ? Infinity : t + POSE_HOLD;
  }

  private now() {
    return (performance.now() - this.t0) / 1000;
  }

  private eyesAt(t: number, p: Pose, sinceSwap: number) {
    // 押したとき（ポーズが1つだけなら、その場で笑って弾む）
    if ((p.react === 'smile' || this.poses.length === 1) && sinceSwap >= 0 && sinceSwap < 1.2 && p.parts.smile) return 'smile';
    for (const b of this.blinks) {
      const d = t - b;
      if (d < -0.1) break;
      if (d >= 0 && d < 0.05) return 'half';
      if (d >= 0.05 && d < 0.12) return 'closed';
      if (d >= 0.12 && d < 0.18) return 'half';
    }
    return 'open';
  }

  private compose(p: Pose, eyes: string) {
    if (p.key === eyes) return;
    p.key = eyes;
    const cx = this.cv.getContext('2d')!;
    cx.clearRect(0, 0, this.cv.width, this.cv.height);
    cx.drawImage(p.img, 0, 0);
    const name = eyes === 'half' && !p.parts.half ? 'closed' : eyes;
    const part = p.parts[name];
    const at = p.eyes?.[name as 'half' | 'closed' | 'smile'];
    if (part && at) cx.drawImage(part, at.x, at.y);
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, p.tex);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.cv);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  }

  private pose(t: number) {
    const m = this.rig.motion ?? 1;
    const headRot = ((noise1(t * 0.35, 1) * 1.8 * m) * Math.PI) / 180;
    const headOff = [noise1(t * 0.3, 2) * 3 * m, noise1(t * 0.4, 3) * 2 * m];
    const ds = t - this.swapAt;
    // 切り替えの弾み：つぶれて伸び、少し跳ねる
    const squash = ds >= 0 && ds < 0.5 ? 0.045 * Math.sin(Math.PI * cl01(ds / 0.16)) - 0.025 * Math.sin(Math.PI * cl01((ds - 0.16) / 0.34)) : 0;
    const hop = ds >= 0.08 && ds < 0.42 ? -22 * m * Math.sin((Math.PI * (ds - 0.08)) / 0.34) : 0;
    const bodyRot = ((noise1(t * 0.22, 4) * 1.0 + 0.45 * Math.sin((2 * Math.PI * t) / 15.5)) * m * Math.PI) / 180;
    const bodyOff = [noise1(t * 0.25, 5) * 5 * m, hop];
    const breath = (Math.sin((t * 2 * Math.PI) / 3.23) * 0.5 + 0.5) * 4 * m;
    return { headRot, headOff, bodyRot, bodyOff, breath, squash: squash * m };
  }

  /** ゆれ（ばね）を固定刻みで進める。大きく飛んだ（タブを戻した）ら直前だけ */
  private secondary(t: number) {
    const dt = 1 / 240;
    if (t - this.simT > 2) this.simT = t - 2;
    while (this.simT < t) {
      this.simT += dt;
      const p = this.pose(this.simT);
      const cur = {
        head: p.headRot * 260 + p.headOff[0] + p.bodyRot * 420 + p.bodyOff[0],
        body: p.bodyRot * 520 + p.bodyOff[0],
        nod: p.headOff[1] + p.bodyOff[1],
        // 胸を動かすもと：体の横の動き・呼吸の持ち上がり・跳ね
        bx: p.bodyRot * 520 + p.bodyOff[0],
        by: -p.breath + p.bodyOff[1] + p.headOff[1] * 0.2,
      };
      if (this.prevMotion) {
        const vh = (cur.head - this.prevMotion.head) / dt;
        const vb = (cur.body - this.prevMotion.body) / dt;
        const vn = (cur.nod - this.prevMotion.nod) / dt;
        const B = this.bust;
        B.v += (-BUST.x.k * B.x - BUST.x.c * B.v - ((cur.bx - this.prevMotion.bx) / dt) * BUST.x.gain) * dt;
        B.x += B.v * dt;
        B.vy += (-BUST.y.k * B.y - BUST.y.c * B.vy - ((cur.by - this.prevMotion.by) / dt) * BUST.y.gain) * dt;
        B.y += B.vy * dt;
        // 呼吸の吸い終わり（いちばん上）ごとに、小さく弾む
        const beat = Math.floor(this.simT / 3.23 - 0.25);
        if (beat !== this.bustBeat) {
          if (this.bustBeat !== -1) B.vy += BUST.beat * (this.rig.motion ?? 1);
          this.bustBeat = beat;
        }
        for (const k of GROUPS) {
          const sp = SPRINGS[k];
          const S = this.st[k];
          const vel = sp.src === 'head' ? vh : vb;
          S.v += (-sp.k * S.x - sp.c * S.v - vel * sp.gain) * dt;
          S.x += S.v * dt;
          S.vy += (-sp.k * 1.4 * S.y - sp.c * 1.3 * S.vy - vn * sp.gain * 0.6) * dt;
          S.y += S.vy * dt;
        }
      }
      this.prevMotion = cur;
    }
    if (this.kick) {
      // ポーズを変えた瞬間：揺れ物を横へはじく
      const dir = Math.random() < 0.5 ? -1 : 1;
      for (const k of GROUPS) this.st[k].v += dir * SPRINGS[k].lim * 9;
      this.bust.vy -= BUST.kick * (this.rig.motion ?? 1);
      this.kick = 0;
    }
    const lim = (v: number, l: number) => l * Math.tanh(v / l);
    this.bustOut = [lim(this.bust.x, BUST.x.lim), lim(this.bust.y, BUST.y.lim)];
    const out = {} as Record<SpringKey, [number, number]>;
    let seed = 11;
    for (const k of GROUPS) {
      const sp = SPRINGS[k];
      const S = this.st[k];
      const x = sp.lim * Math.tanh((S.x + 0.25 * sp.idle[0] * noise1(t * 0.4, seed++)) / sp.lim);
      const y = sp.lim * 0.5 * Math.tanh(S.y / (sp.lim * 0.5));
      out[k] = [x, Math.abs(x) * -0.12 + y];
    }
    return out;
  }

  private frame = () => {
    this.raf = 0;
    if (this.destroyed || !this.canvas.isConnected) return;
    if (!document.hidden) this.render();
    this.raf = requestAnimationFrame(this.frame);
  };

  private fit() {
    const c = this.canvas;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.round(c.clientWidth * dpr);
    const h = Math.round(c.clientHeight * dpr);
    if (w > 0 && h > 0 && (c.width !== w || c.height !== h)) {
      c.width = w;
      c.height = h;
    }
  }

  private render() {
    this.fit();
    const gl = this.gl;
    const t = this.now();
    if (t >= this.backAt) {
      if (this.rig.tap) {
        this.backAt = Infinity;
        this.fadeTo(this.home, t);
      } else this.swapTo(0, t);
    }
    const [W, H] = this.rig.size;
    const ds = t - this.swapAt;
    const p = this.pose(t);
    const sec = this.secondary(t);
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);
    const cw = gl.drawingBufferWidth;
    const ch = gl.drawingBufferHeight;
    const { s, ox, oy } = this.layout(cw, ch);
    const sx = (s * 2) / cw;
    const sy = (-s * 2) / ch;
    gl.uniformMatrix3fv(this.loc.u_view, false, new Float32Array([sx, 0, 0, 0, sy, 0, (ox * 2) / cw - 1, 1 - (oy * 2) / ch, 1]));
    gl.uniform2f(this.loc.u_size, W, H);
    gl.uniform1f(this.loc.u_headRot, p.headRot);
    gl.uniform2f(this.loc.u_headOff, p.headOff[0], p.headOff[1]);
    gl.uniform1f(this.loc.u_time, t);
    gl.uniform1f(this.loc.u_breath, p.breath);
    gl.uniform1f(this.loc.u_bodyRot, p.bodyRot);
    gl.uniform2f(this.loc.u_bodyOff, p.bodyOff[0], p.bodyOff[1]);
    gl.uniform1f(this.loc.u_squash, p.squash);
    const bust = this.rig.bust;
    gl.uniform2f(this.loc.u_bustC0, ...(bust?.c[0] ?? [0, 0]));
    gl.uniform2f(this.loc.u_bustC1, ...(bust?.c[1] ?? [0, 0]));
    gl.uniform2f(this.loc.u_bustR, ...(bust?.r ?? [0, 0]));
    gl.uniform2f(this.loc.u_bust, this.bustOut[0], this.bustOut[1] - p.breath * BUST.lift);
    const amp = (k: SpringKey) => (this.rig.sway?.[k] ?? 1) * (this.rig.motion ?? 1);
    const sw = (k: SpringKey) => [sec[k][0] * amp(k), sec[k][1] * amp(k)] as const;
    const idl = (k: SpringKey) => [SPRINGS[k].idle[0] * amp(k), SPRINGS[k].idle[1], SPRINGS[k].idle[2]] as const;
    gl.uniform2f(this.loc.u_sw0, ...sw('hair'));
    gl.uniform2f(this.loc.u_sw1, ...sw('cloth'));
    gl.uniform2f(this.loc.u_sw2, ...sw('prop'));
    gl.uniform3f(this.loc.u_id0, ...idl('hair'));
    gl.uniform3f(this.loc.u_id1, ...idl('cloth'));
    gl.uniform3f(this.loc.u_id2, ...idl('prop'));
    gl.uniform1f(this.loc.u_wind, 1.2 * (this.rig.motion ?? 1));
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const draw = (pz: Pose, alpha: number, flash: number) => {
      this.compose(pz, this.eyesAt(t, pz, ds));
      gl.uniform2f(this.loc.u_pivot, ...(pz.neck ?? this.rig.neck));
      gl.uniform2f(this.loc.u_hip, ...(pz.hip ?? this.rig.hip));
      gl.uniform1f(this.loc.u_alpha, alpha);
      gl.uniform1f(this.loc.u_flash, flash);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, pz.tex);
      gl.uniform1i(this.loc.u_tex, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, pz.wTex);
      gl.uniform1i(this.loc.u_w, 1);
      gl.drawElements(gl.TRIANGLES, this.nIdx, gl.UNSIGNED_SHORT, 0);
    };
    // 表情の入れ替え（setPose・女将を押したとき）：前の表情の上に新しい表情を重ねて濃くしていく
    const df = t - this.fadeAt;
    if (this.fadeFrom >= 0 && df >= 0 && df < FADE) {
      const k = df / FADE;
      draw(this.poses[this.fadeFrom], 1, 0);
      draw(this.poses[this.cur], k * k * (3 - 2 * k), 0);
      gl.bindVertexArray(null);
      return;
    }
    // 切り替え：古い絵が一瞬白く光り、光りきった所で新しい絵に入れ替えて光が引く（2枚を重ねない＝二重に見えない）
    const swapping = this.prev !== this.cur && ds >= 0 && ds < SWAP;
    const changed = this.prev !== this.cur;
    const flash = !changed || ds < 0 ? 0 : ds < SWAP ? 0.75 * (ds / SWAP) : 0.75 * (1 - cl01((ds - SWAP) / 0.28));
    draw(this.poses[swapping ? this.prev : this.cur], 1, flash);
    gl.bindVertexArray(null);
  }
}

const cache = new Map<LiveId, Promise<LivePortrait | null>>();
/** キャラごとに1つだけ作って使い回す */
export function livePortrait(id: LiveId): Promise<LivePortrait | null> {
  let p = cache.get(id);
  if (!p) {
    p = LivePortrait.load(id);
    cache.set(id, p);
  }
  return p;
}
